import { useEffect, useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@material-ui/core";

import UtilsFncs from "utils/UtilsFncs";
import IntroBrandScreen from "./components/IntroBrandScreen";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import ProductList from "./components/ProductList";
import Footer from "./components/Footer";
import GridModal from "./components/GridModal";
import CartModal from "./components/CartModal";
import OrdersModal from "./components/OrdersModal";
import ConfirmModal from "./components/ConfirmModal";
import AlertModal from "./components/AlertModal";

import "./styles.scss";
import useDialogs from "./hooks/useDialogs";
import useVitrineData from "./hooks/useVitrineData";
import useBrandCart, { mergeOrderStateAcrossBrands } from "./hooks/useBrandCart";
import useOrderSubmit from "./hooks/useOrderSubmit";
import useStoreSelection from "./hooks/useStoreSelection";
import useProductFilters from "./hooks/useProductFilters";
import useGridModalState from "./hooks/useGridModalState";
import useOrdersHistory from "./hooks/useOrdersHistory";
import { resolveOrderLineFromKey } from "./hooks/resolveOrderLine";

function isTokenExpiredOrMissing() {
  try {
    const session = JSON.parse(
      localStorage.getItem("ApiCredentials") || "null",
    );
    if (!session?.accessToken || !session?.dtExpiration) return true;
    const dtExp = new Date(session.dtExpiration).getTime();
    const dtNow = new Date().getTime();
    return dtNow >= dtExp;
  } catch {
    return true;
  }
}

export default function VitrineEmpresaPage() {
  useEffect(() => {
    if (isTokenExpiredOrMissing()) {
      localStorage.setItem("sessionExpired", "true");
      UtilsFncs.RedirectToLoginPage();
    }
  }, []);

  const dialogs = useDialogs();
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [selectedIdPessoaLoja, setSelectedIdPessoaLoja] = useState(null);

  const {
    loading,
    vitrineApiResult,
    effectiveFamilies,
    effectiveBusinessRules,
    effectiveBrandKeys,
    effectiveBrandLabels,
    vitrinesMeta,
    primaryVitrine,
    brandKeysWithProducts,
    availableBrandKeys,
  } = useVitrineData({
    showAlert: dialogs.showAlert,
    idPessoaLoja: selectedIdPessoaLoja,
  });

  const cart = useBrandCart({
    vitrineApiResult,
    effectiveBrandKeys,
    availableBrandKeys,
    primaryVitrine,
    effectiveFamilies,
    openConfirm: dialogs.openConfirm,
    closeConfirm: dialogs.closeConfirm,
  });

  const idsVitrineNoCarrinho = useMemo(() => {
    // Junta a marca ativa com o carrinho salvo de outras marcas (useBrandCart persiste por
    // marca) — senão, um pedido com vitrines de marcas diferentes só considerava a loja da
    // marca ativa na seleção de loja.
    const { fullOrderState } = mergeOrderStateAcrossBrands({
      orderState: cart.orderState,
      vitrinePaymentConditionState: cart.vitrinePaymentConditionState,
      currentBrand: cart.currentBrand,
      brandKeys: effectiveBrandKeys,
    });
    const ids = new Set();
    Object.entries(fullOrderState).forEach(([key, qty]) => {
      if (qty == null || Number(qty) <= 0) return;
      const resolved = resolveOrderLineFromKey(key, effectiveFamilies);
      if (resolved?.prod?.idVitrineVirtualB2b != null) {
        ids.add(resolved.prod.idVitrineVirtualB2b);
      }
    });
    return [...ids].sort((a, b) => a - b);
  }, [
    cart.orderState,
    cart.vitrinePaymentConditionState,
    cart.currentBrand,
    effectiveBrandKeys,
    effectiveFamilies,
  ]);

  const storeSelection = useStoreSelection({
    idsVitrineNoCarrinho,
    selectedIdPessoaLoja,
    onSelectStore: setSelectedIdPessoaLoja,
  });

  const { submitOrderLoading, submitOrder } = useOrderSubmit({
    orderState: cart.orderState,
    vitrinePaymentConditionState: cart.vitrinePaymentConditionState,
    effectiveFamilies,
    setOrderState: cart.setOrderState,
    setVitrinePaymentConditionState: cart.setVitrinePaymentConditionState,
    currentBrand: cart.currentBrand,
    effectiveBrandKeys,
    showAlert: dialogs.showAlert,
    openConfirm: dialogs.openConfirm,
    closeConfirm: dialogs.closeConfirm,
    onSuccess: () => setCartModalOpen(false),
    selectedIdPessoaLoja: storeSelection.selectedIdPessoaLoja,
    requiresStoreSelection: storeSelection.requiresStoreSelection,
  });

  const filters = useProductFilters({
    effectiveFamilies,
    currentBrand: cart.currentBrand,
  });

  const gridModal = useGridModalState({
    orderState: cart.orderState,
    setOrderState: cart.setOrderState,
  });

  const ordersHistory = useOrdersHistory({
    vitrinesMeta,
    currentBrand: cart.currentBrand,
  });

  const { theme } = cart;

  if (loading && !vitrineApiResult) {
    return (
      <Box
        className="fazer-pedido-page vitrine-empresa-loading"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress size={48} />
        <Typography style={{ marginTop: 16 }}>Carregando vitrine...</Typography>
      </Box>
    );
  }

  return (
    <Box
      className={`theme-${cart.currentBrand === "karsten_decor" ? "karsten" : cart.currentBrand} fazer-pedido-page`}
    >
      {cart.shouldShowIntroScreen && (
        <IntroBrandScreen
          onSelectBrand={cart.handleSelectBrand}
          brandKeysWithProducts={brandKeysWithProducts}
        />
      )}
      {!cart.shouldShowIntroScreen && primaryVitrine && cart.currentBrand && (
        <>
          <Header
            selectedBrand={cart.currentBrand}
            theme={theme}
            onSwitchBrand={
              cart.canSwitchBrand ? cart.handleRequestShowIntro : undefined
            }
            allowBrandSwitch={cart.canSwitchBrand}
            currentBrandLabel={
              effectiveBrandLabels[cart.currentBrand] || cart.currentBrand
            }
          />
          <Footer
            totals={cart.totals}
            clearOrder={cart.clearOrder}
            openCartModal={() => setCartModalOpen(true)}
            openOrdersModal={() => ordersHistory.setOrdersModalOpen(true)}
            theme={theme}
          />
          <Box className="main-container">
            <Sidebar
              currentBrand={cart.currentBrand}
              families={effectiveFamilies}
              selectedAgrupadores={filters.selectedAgrupadores}
              selectedCommercialNames={filters.selectedCommercialNames}
              selectedVitrines={filters.selectedVitrines}
              selectedSizes={filters.selectedSizes}
              productSearchTerm={filters.productSearchTerm}
              setProductSearchTerm={filters.setProductSearchTerm}
              toggleFilter={filters.toggleFilter}
              theme={theme}
              vitrinesMeta={vitrinesMeta}
            />
            <Box className="main-content">
              <ProductList
                currentBrand={cart.currentBrand}
                families={filters.scopedFamilies}
                filteredFamilies={filters.filteredFamilies}
                orderState={cart.orderState}
                setOrderState={cart.setOrderState}
                openGridModal={gridModal.openGridModal}
                productSearchTerm={filters.productSearchTerm}
                theme={theme}
              />
            </Box>
          </Box>
          <GridModal
            open={gridModal.gridModalOpen}
            onClose={gridModal.closeGridModal}
            onSave={gridModal.saveGridSelection}
            currentBrand={cart.currentBrand}
            activeFamilyKey={gridModal.activeFamilyKey}
            families={filters.scopedFamilies}
            tempGridState={gridModal.tempGridState}
            setTempGridState={gridModal.setTempGridState}
            selectedBulkAgrupadores={gridModal.selectedBulkAgrupadores}
            selectedBulkSizes={gridModal.selectedBulkSizes}
            toggleBulkSelection={gridModal.toggleBulkSelection}
            theme={theme}
          />
          <CartModal
            open={cartModalOpen}
            onClose={() => setCartModalOpen(false)}
            currentBrand={cart.currentBrand}
            families={effectiveFamilies}
            orderState={cart.orderState}
            vitrinePaymentConditionState={cart.vitrinePaymentConditionState}
            setVitrinePaymentCondition={cart.setVitrinePaymentCondition}
            isCartGrouped={cart.isCartGrouped}
            toggleCartGrouping={cart.toggleCartGrouping}
            removeFromCart={cart.removeFromCart}
            submitOrder={submitOrder}
            submitOrderLoading={submitOrderLoading}
            businessRules={effectiveBusinessRules}
            theme={theme}
            vitrinesMeta={vitrinesMeta}
            requiresStoreSelection={storeSelection.requiresStoreSelection}
            singleStoreName={storeSelection.singleStoreName}
            storeOptions={storeSelection.storeOptions}
            selectedIdPessoaLoja={storeSelection.selectedIdPessoaLoja}
            onSelectStore={storeSelection.selectStore}
            loadingStores={storeSelection.loadingStores}
            vitrineRefreshing={loading}
          />
          <OrdersModal
            open={ordersHistory.ordersModalOpen}
            onClose={() => ordersHistory.setOrdersModalOpen(false)}
            pedidos={ordersHistory.pedidosList}
            loading={ordersHistory.loadingPedidos}
            fetchPedidoDetalhe={ordersHistory.fetchPedidoDetalhe}
            theme={theme}
          />
          <ConfirmModal
            open={dialogs.confirmModalOpen}
            onClose={() => dialogs.setConfirmModalOpen(false)}
            onConfirm={dialogs.runConfirm}
            title={dialogs.confirmData.title}
            message={dialogs.confirmData.message}
            theme={theme}
          />
        </>
      )}
      <AlertModal
        open={dialogs.alertModalOpen}
        onClose={() => dialogs.setAlertModalOpen(false)}
        title={dialogs.alertTitle}
        message={dialogs.alertMessage}
        theme={theme}
        type={dialogs.alertType}
      />
    </Box>
  );
}
